/**
 * WritingHistoryPage — the writing-sessions LANDING page ("My Writing
 * Sessions").
 *
 * Implements show-not-tell/TS-014 (T6, AC-TS014.1, AC-TS014.3, AC-TS014.5).
 * Originally a VIEW-ONLY history page reached from the WritingPracticeTile's
 * "See all my writing sessions" link. Mirrors SessionsListPage's warm-header
 * + max-w-4xl scaffolding (frontend/src/pages/SessionsListPage.tsx) and
 * reuses the shared LoadingState/ErrorState/EmptyState components verbatim,
 * plus the shared WritingSessionRow (T5) for each row so tapping a row
 * reopens via the existing US-011 view — not a new viewer (AC-TS014.5).
 *
 * Modified by BUG-20260718-154634 (C-2): upgraded from view-only history
 * into the writing-coach's LANDING page, MIRRORING comprehension's
 * SessionsListPage shape (useSessions + useStartSession +
 * ResumeSessionHero — a READ-ONLY pattern reference, never imported/edited
 * here per BR-TS004.2 / BR-009 slice isolation): (a) a "Start a new
 * session" action that calls show-not-tell's OWN useWritingSession.start()
 * then navigates to ROUTES.WRITING_PRACTICE (the SAME start flow
 * WritingPracticePage's own idle-state button already uses — no new start
 * mechanism); (b) a resume affordance (WritingResumeHero, C-2's own mirror
 * of ResumeSessionHero) shown when the caller has an active session (C-3's
 * additive `activeSession` field on GET /history); (c) the existing
 * paginated completed/terminal-session list (useWritingHistory +
 * WritingSessionRow) below, with the active session's own row filtered out
 * (it is already rendered by the hero) so it never appears twice — mirrors
 * comprehension's useSessions() splitting activeSessions from
 * completedSessions.
 *
 * This is now the dashboard hero's CTA target (WritingPracticeHeroCard,
 * C-1) via ROUTES.WRITING_PRACTICE_HISTORY, replacing the prior direct jump
 * to /writing-practice — closing the "no listing stop" UX gap the ticket
 * describes.
 *
 * Route registered in App.tsx INSIDE the existing
 * <FeatureGuard feature="show_not_tell"> block (same gate as
 * /writing-practice, BR-TS004.1) — the operability atom that makes this page
 * (and the tile's link, and now the dashboard hero's CTA) actually
 * reachable.
 */

import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { useWritingHistory } from '@/hooks/useWritingHistory';
import { useAuth } from '@/hooks/useAuth';
import { useWritingSession } from '@/hooks/useWritingSession';
import { AppLogo } from '@/components/AppLogo';
import { LogoutButton } from '@/components/LogoutButton';
import { LoadingState } from '@/components/LoadingState';
import { ErrorState } from '@/components/ErrorState';
import { EmptyState } from '@/components/EmptyState';
import { WritingSessionRow } from '@/components/WritingSessionRow';
import { WritingResumeHero } from '@/components/WritingResumeHero';
import { Button } from '@/components/ui/button';
import { getPostLoginRoute, ROUTES } from '@/constants/routes';

export function WritingHistoryPage() {
  const navigate = useNavigate();
  // Back link follows the viewer's role rather than the deprecated static
  // /dashboard: parents -> /parent, students -> /dashboard. Delegates the fork
  // to getPostLoginRoute (the same helper used by AppLogo / RootRedirect /
  // LoginPage) so the precedence rule stays in one place (TO-LL-013).
  const { user } = useAuth();
  const backTarget = getPostLoginRoute(user?.groups);
  const {
    items,
    activeSession,
    isLoading,
    isError,
    activeError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    refetchActive,
  } = useWritingHistory();

  // BUG-20260718-154634 (C-2): a dedicated useWritingSession instance owned
  // by THIS page, used ONLY to drive the "Start a new session" action. This
  // is show-not-tell's OWN existing start flow (the same start() the
  // WritingPracticePage idle-state button already calls) — not a new
  // start mechanism, and never imported from comprehension.
  const session = useWritingSession();

  // Once start() succeeds, session.status flips to 'active' (and the active
  // session id is persisted to sessionStorage inside useWritingSession) —
  // navigate to the coaching page, which restores the just-started session
  // on mount (TO-LL-013: validated internal ROUTES constant, never a path
  // literal; TO-LL-026: navigation happens only after the mutation's own
  // success signal, not optimistically).
  useEffect(() => {
    if (session.status === 'active') {
      navigate(ROUTES.WRITING_PRACTICE);
    }
  }, [session.status, navigate]);

  // BUG-20260718-154634 (C-2): the active session (if any) is surfaced by
  // the hero above — filter its row out of the completed/terminal list
  // below so it is never shown twice, mirroring comprehension's
  // useSessions() split of activeSessions vs completedSessions.
  const historyItems = activeSession
    ? items.filter((item) => item.sessionId !== activeSession.sessionId)
    : items;

  return (
    <div className="otter-bg font-body min-h-screen text-otter-earth-800">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-otter-sand bg-otter-paper/80 px-3 py-2 backdrop-blur-md md:px-6 md:py-3">
        <div className="flex items-center gap-2">
          <Link
            to={backTarget}
            className="inline-flex items-center gap-0.5 rounded-lg px-2 py-1.5 text-sm font-bold text-pebbles-500 transition-colors hover:bg-otter-sand/40 hover:text-otter-earth-800"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back</span>
          </Link>
          <AppLogo />
        </div>
        <LogoutButton />
      </header>

      {/* ── Main ────────────────────────────────────────────────────────── */}
      <main className="mx-auto max-w-4xl">
        <div className="px-4 pb-8 pt-4 md:px-6 md:pt-7">
          <h1 className="font-display text-2xl font-semibold leading-tight text-otter-earth-800 md:text-[32px]">
            My Writing Sessions
          </h1>
          <p className="mt-1 text-sm font-medium text-gray-500 md:text-[15px]">
            Start a new session, continue one in progress, or revisit a past one.
          </p>

          <div className="mt-5">
            {isLoading && <LoadingState message="Loading your writing sessions..." />}

            {isError && !isLoading && (
              <ErrorState
                message="Something hiccuped loading your writing history. Let's try that again."
                onRetry={refetch}
              />
            )}

            {!isLoading && !isError && (
              <>
                {/* Start-or-resume hero — active session leads (BR-TS006.1
                    one-active-per-child); otherwise a "Start a new session"
                    action, mirroring SessionsListPage's hasActiveSession
                    branch (frontend/src/pages/SessionsListPage.tsx).
                    BUG-20260718-172622 (T-5): a LEADING branch handles the
                    /active read genuinely ERRORING (as opposed to
                    resolving to a real null) — an infra failure here must
                    never masquerade as "no active session / Start"; the
                    history list below still renders regardless. */}
                {activeError ? (
                  <ErrorState
                    message="We couldn't check whether you have a session to resume. Let's try again."
                    onRetry={refetchActive}
                  />
                ) : activeSession ? (
                  <WritingResumeHero activeSession={activeSession} />
                ) : (
                  <div className="relative mb-2 flex flex-col overflow-hidden rounded-3xl border border-orange-200 bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 p-6 shadow-lg">
                    <div
                      aria-hidden="true"
                      className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-otter-orange-400/20 blur-2xl"
                    />
                    <p className="relative z-[1] mb-4 text-[11px] font-extrabold uppercase tracking-wider text-otter-orange-600">
                      <span aria-hidden="true">🦦</span> New session
                    </p>
                    <div className="relative z-[1] flex flex-wrap items-center justify-between gap-4">
                      <div className="min-w-0">
                        <h2 className="font-display text-xl font-semibold leading-tight text-otter-earth-800">
                          Ready to turn telling into showing?
                        </h2>
                        <p className="mt-1 text-[13px] font-semibold text-orange-700/80">
                          Your writing coach is ready when you are.
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="warm"
                        size="lg"
                        onClick={() => session.start()}
                        disabled={session.isStarting}
                        className="h-12 px-6"
                        aria-busy={session.isStarting}
                        aria-label="Start a new writing session"
                      >
                        {session.isStarting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Starting session...
                          </>
                        ) : (
                          'Start a new session'
                        )}
                      </Button>
                    </div>
                    {session.error && session.error.kind === 'retry' && (
                      <div className="relative z-[1] mt-4" role="alert" aria-live="polite">
                        <p className="text-sm text-red-600">{session.error.message}</p>
                        <button
                          type="button"
                          className="mt-1 text-sm text-tutor-600 underline"
                          onClick={() => session.retry()}
                        >
                          Try again
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Past/terminal sessions list (all statuses except the
                    active one already shown above), unchanged from the
                    prior view-only history page's own list rendering. */}
                {historyItems.length === 0 && (
                  <EmptyState message="No writing practice yet — try your first one!" />
                )}

                {historyItems.length > 0 && (
                  <>
                    <ul className="space-y-2.5" role="list">
                      {historyItems.map((row) => (
                        <li key={row.sessionId}>
                          <WritingSessionRow row={row} />
                        </li>
                      ))}
                    </ul>

                    {hasNextPage && (
                      <div className="mt-4 flex justify-center">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fetchNextPage()}
                          disabled={isFetchingNextPage}
                          aria-busy={isFetchingNextPage}
                          className="min-h-[44px]"
                        >
                          {isFetchingNextPage ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Loading more...
                            </>
                          ) : (
                            'Load more'
                          )}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
